<template>
    <div class="backdrop" @click="closeSidebar" @touchstart.passive="closeSidebar"></div>

    <!-- STICKY TOP NAVBAR (Solo Logo y Datos de Usuario) -->
    <header class="db-header border-t border-b z-50 transition-all duration-300" :class="{ 'xh-scrolled-shadow': isScrolled }">
        <router-link class="nav-brand sm:h-12 p-0 md:pl-1 flex justify-center items-center" :to="{ name: 'frontend.home' }">
            <img v-if="setting.theme_logo" class="logo" :src="setting.theme_logo" alt="logo">
        </router-link>
        <div class="flex items-center justify-end w-full gap-2">
            <!-- Sub-header para vista de Escritorio -->
            <div class="sub-header md:flex items-center gap-4 transition">
                <div class="flex items-center justify-center gap-2">
                    <div v-if="setting.site_language_switch === enums.activityEnum.ENABLE"
                        class="dropdown-group relative">
                        <button type="button" class="dropdown-btn w-9 h-9 rounded-lg flex items-center justify-center text-primary bg-primary/5 hover:bg-primary/10 transition">
                            <img v-if="language && language.image" :src="language.image" alt="flag" class="w-5 h-5 rounded-full object-cover">
                            <i v-else class="fa-solid fa-globe text-primary text-sm"></i>
                        </button>
                        <ul v-if="languages.length > 0"
                            class="p-2 min-w-[160px] rounded-lg shadow-xl absolute top-12 ltr:-right-2 rtl:-left-2 z-50 border border-gray-200 bg-white transition-all duration-300 dropdown-list">
                            <li v-for="(langItem, index) in languages" :key="index"
                                @click.prevent="changeLanguage(langItem.id, langItem.code, langItem.display_mode)"
                                class="flex items-center gap-2 py-1.5 px-2.5 rounded-md cursor-pointer hover:bg-gray-100 transition"
                                :class="{ 'bg-gray-100 font-semibold': language && language.id === langItem.id }">
                                <img :src="langItem.image" alt="flag" class="w-4 h-4 rounded-full object-cover">
                                <span class="text-heading capitalize text-xs font-medium">{{ langItem.name }}</span>
                            </li>
                        </ul>
                    </div>

                    <!-- Campana de Notificaciones Internas -->
                    <div class="dropdown-group relative">
                        <button type="button" class="dropdown-btn w-9 h-9 rounded-lg flex items-center justify-center text-primary bg-primary/5 hover:bg-primary/10 transition relative" title="Notificaciones">
                            <i class="fa-regular fa-bell text-sm"></i>
                            <span v-if="unreadCount > 0" class="absolute -top-1 -right-1 w-3 h-3 bg-warning rounded-full border-2 border-white shadow-sm animate-pulse"></span>
                        </button>
                        <div class="dropdown-list w-[360px] max-w-[90vw] p-0 rounded-lg shadow-md absolute top-12 ltr:-right-2 rtl:-left-2 z-50 border border-slate-200 bg-white overflow-hidden text-left">
                            <div class="px-4 py-3 bg-slate-50 border-b border-slate-200 flex items-center justify-between">
                                <div class="flex items-center gap-2">
                                    <h4 class="text-xs font-bold uppercase tracking-wider text-slate-700">Notificaciones</h4>
                                    <span v-if="unreadCount > 0" class="px-1.5 py-0.5 rounded text-[10px] font-bold bg-primary/10 text-primary">
                                        {{ unreadCount }} nuevas
                                    </span>
                                </div>
                                <button v-if="unreadCount > 0" @click.prevent="markAllNotificationsAsRead" type="button" class="text-[11px] text-primary hover:underline font-medium">
                                    Marcar leídas
                                </button>
                            </div>
                            <div class="p-4 max-h-[360px] overflow-y-auto divide-y divide-slate-100">
                                <template v-if="internalNotifications.length > 0">
                                    <div
                                        v-for="item in internalNotifications"
                                        :key="item.id"
                                        @click="handleNotificationClick(item)"
                                        class="p-3 hover:bg-slate-50 cursor-pointer transition flex items-start gap-3"
                                        :class="{ 'bg-primary/[0.03]': !item.read_at }"
                                    >
                                        <div class="w-8 h-8 rounded-lg flex items-center justify-center flex-shrink-0 text-sm"
                                            :class="item.type === 'new_order' ? 'bg-amber-100 text-amber-700' : 'bg-blue-100 text-blue-700'">
                                            <i :class="item.icon || (item.type === 'new_order' ? 'fa-solid fa-bag-shopping' : 'fa-solid fa-user')"></i>
                                        </div>
                                        <div class="flex-1 min-w-0">
                                            <div class="flex items-center justify-between gap-1 mb-0.5">
                                                <p class="text-xs font-semibold text-slate-800 truncate" :class="{ 'font-bold': !item.read_at }">
                                                    {{ item.title }}
                                                </p>
                                                <span v-if="!item.read_at" class="w-2 h-2 rounded-full bg-primary flex-shrink-0"></span>
                                            </div>
                                            <p class="text-xs text-slate-600 line-clamp-2 leading-tight">
                                                {{ item.message }}
                                            </p>
                                            <span class="text-[10px] text-slate-400 mt-1 block">
                                                {{ item.created_at }}
                                            </span>
                                        </div>
                                    </div>
                                </template>
                                <div v-else class="py-8 px-4 text-center text-slate-400">
                                    <i class="fa-regular fa-bell-slash text-2xl mb-2 block opacity-40"></i>
                                    <p class="text-xs">No tienes notificaciones por ahora</p>
                                </div>
                            </div>
                        </div>
                    </div>

                    <router-link v-if="pos.permission"
                        class="w-9 h-9 rounded-lg flex items-center justify-center text-primary bg-primary/5"
                        :to="{ path: '/admin/' + pos.url }">
                        <i class="lab lab-fill-pos lab-font-size-16 font-fill-pos"></i>
                    </router-link>

                    <!-- Botón de menú para escritorio -->
                    <button @click="toggleSidebar" :class="isSidebarActive ? 'fa-xmark text-lg' : 'fa-align-left'" class="hidden md:flex fa-solid db-header-nav w-9 h-9 rounded-lg text-primary bg-primary/5 items-center justify-center transition-all"></button>
                </div>

            </div>

            <!-- Dropdown de datos de usuario -->
            <div class="hidden sm:block dropdown-group border rounded-lg">
                <button class="dropdown-btn flex items-center gap-1">
                    <img class="flex-shrink-0 w-9 h-9 object-cover rounded-lg filter-gray p-1" :src="authInfo.image" alt="avatar">
                    <h3 class="whitespace-nowrap text-sm font-normal capitalize text-left leading-[12px] pr-1">{{ $t('label.hello') }} <b
                            class="block font-medium text-primary">{{ textShortener(authInfo.name, 15) }}</b></h3>
                    <i class="lab lab-arrow-down text-xs ml-1.5 lab-font-size-14"></i>
                </button>
                <div class="dropdown-list fixed sm:absolute top-[65px] sm:top-12 ltr:right-0 rtl:left-0 sm:w-[360px] z-[70] rounded-xl max-h-[calc(100vh_-_90px)] sm:max-h-none overflow-y-auto sm:h-auto p-4 shadow-paper bg-white border border-gray-100">
                    <div class="w-fit mx-auto text-center mb-5">
                        <figure
                            class="relative z-10 w-[98px] h-[98px] border-2 border-dashed rounded-full inline-flex items-center justify-center border-white bg-gradient-to-t from-[#FF7A00] to-[#FF016C] before:absolute before:top-1/2 before:left-1/2 before:-translate-x-1/2 before:-translate-y-1/2 before:w-24 before:h-24 before:rounded-full before:-z-10 before:bg-white">
                            <img class="w-[90px] h-[90px] rounded-full shadow-avatar" :src="authInfo.image"
                                alt="avatar">
                        </figure>

                        <label for="imageProperty"
                            class="block w-11 h-11 mx-auto -mt-7 mb-3 relative z-10 rounded-full border-2 cursor-pointer bg-heading border-white">
                            <input @change="saveImage" accept="image/png, image/jpeg, image/jpg" ref="imageProperty"
                                type="file" id="imageProperty"
                                class="w-full h-full rounded-full opacity-0 cursor-pointer">
                            <i
                                class="lab lab-fill-edit absolute top-1/2 left-1/2 -translate-y-1/2 -translate-x-1/2 -z-10 lab-font-size-24 lab-font-color-1 cursor-pointer"></i>
                        </label>

                        <h3 class="font-medium text-sm leading-6 capitalize mb-0.5">
                            {{ textShortener(authInfo.name, 20) }}
                        </h3>
                        <p class="text-xs mb-0.5">{{ authInfo.email }}</p>
                        <p v-if="authInfo.phone" class="text-xs" dir="ltr">{{ authInfo.country_code }}{{ authInfo.phone
                            }}</p>
                        <h3 class="font-medium text-sm leading-6 capitalize mb-0.5">{{ authInfo.currency_balance }}</h3>
                    </div>
                    <nav>
                        <router-link :to="{ name: 'admin.profile.editProfile' }"
                            class="paper-link transition w-full flex items-center gap-3.5 py-3 border-b last:border-none border-[#EFF0F6]">
                            <i class="lab lab-line-edit lab-font-size-17"></i>
                            <span class="text-sm leading-6 capitalize">{{ $t('button.edit_profile') }}</span>
                        </router-link>

                        <router-link :to="{ name: 'admin.profile.changePassword' }"
                            class="paper-link transition w-full flex items-center gap-3.5 py-3 border-b last:border-none border-[#EFF0F6]">
                            <i class="lab lab-line-key lab-font-size-17"></i>
                            <span class="text-sm leading-6 capitalize">{{ $t('button.change_password') }}</span>
                        </router-link>

                        <button @click="logout()"
                            class="paper-link transition w-full flex items-center gap-3.5 py-3 border-b last:border-none border-[#EFF0F6]">
                            <i class="lab lab-line-logout lab-font-size-17"></i>
                            <span class="text-sm leading-6 capitalize">{{ $t('button.logout') }}</span>
                        </button>
                    </nav>
                </div>
            </div>

            <!-- Botón de menú (transparente, borde gris) -->
            <div class="sm:hidden pr-1">
                <button @click="toggleSidebar" :class="isSidebarActive ? 'fa-xmark text-lg' : 'fa-align-left'" class="fa-solid db-header-nav w-9 h-9 flex-shrink-0 rounded-lg text-primary bg-transparent border border-gray-200 flex items-center justify-center transition-all"></button>

            </div>
        </div>
    </header>

    <!-- BARRA SECUNDARIA PARA MÓVIL (HACE SCROLL CON EL CUERPO DE LA PÁGINA) -->
    <div class="sub-header-mobile flex md:hidden items-center justify-between px-2 py-3 border-b border-gray-200 bg-white w-full">
        <!-- Iconos de idioma y vista POS -->
        <div class="flex items-center gap-2 flex-shrink-0">
            <div v-if="setting.site_language_switch === enums.activityEnum.ENABLE"
                class="dropdown-group relative">
                <button type="button" class="dropdown-btn w-9 h-9 rounded-lg flex items-center justify-center text-primary bg-primary/5 hover:bg-primary/10 transition">
                    <img v-if="language && language.image" :src="language.image" alt="flag" class="w-5 h-5 rounded-full object-cover">
                    <i v-else class="fa-solid fa-globe text-primary text-sm"></i>
                </button>
                <ul v-if="languages.length > 0"
                    class="p-2 min-w-[160px] rounded-lg shadow-xl absolute top-12 ltr:right-0 rtl:left-0 z-50 border border-gray-200 bg-white transition-all duration-300 dropdown-list">
                    <li v-for="(langItem, index) in languages" :key="index"
                        @click.prevent="changeLanguage(langItem.id, langItem.code, langItem.display_mode)"
                        class="flex items-center gap-2 py-1.5 px-2.5 rounded-md cursor-pointer hover:bg-gray-100 transition"
                        :class="{ 'bg-gray-100 font-semibold': language && language.id === langItem.id }">
                        <img :src="langItem.image" alt="flag" class="w-4 h-4 rounded-full object-cover">
                        <span class="text-heading capitalize text-xs font-medium">{{ langItem.name }}</span>
                    </li>
                </ul>
            </div>

                    <!-- Campana de Notificaciones Móvil -->
                    <div class="dropdown-group relative">
                        <button type="button" class="dropdown-btn w-9 h-9 rounded-lg flex items-center justify-center text-primary bg-primary/5 hover:bg-primary/10 transition relative" title="Notificaciones">
                            <i class="fa-regular fa-bell text-sm"></i>
                            <span v-if="unreadCount > 0" class="absolute top-2 right-2 w-2 h-2 bg-red-500 rounded-full border border-white shadow-sm animate-pulse"></span>
                        </button>
                        <div class="dropdown-list w-[320px] max-w-[85vw] p-0 rounded-xl shadow-2xl fixed sm:absolute top-[115px] sm:top-12 left-4 z-50 border border-slate-200 bg-white overflow-hidden text-left">
                            <div class="px-4 py-3 bg-slate-50 border-b border-slate-200 flex items-center justify-between">
                                <div class="flex items-center gap-2">
                                    <h4 class="text-xs font-bold uppercase tracking-wider text-slate-700">Notificaciones</h4>
                                    <span v-if="unreadCount > 0" class="px-1.5 py-0.5 rounded text-[10px] font-bold bg-primary/10 text-primary">
                                        {{ unreadCount }}
                                    </span>
                                </div>
                                <button v-if="unreadCount > 0" @click.prevent="markAllNotificationsAsRead" type="button" class="text-[11px] text-primary hover:underline font-medium">
                                    Marcar leídas
                                </button>
                            </div>
                            <div class="max-h-[300px] overflow-y-auto divide-y divide-slate-100">
                                <template v-if="internalNotifications.length > 0">
                                    <div
                                        v-for="item in internalNotifications"
                                        :key="'m-' + item.id"
                                        @click="handleNotificationClick(item)"
                                        class="p-3 hover:bg-slate-50 cursor-pointer transition flex items-start gap-3"
                                        :class="{ 'bg-primary/[0.03]': !item.read_at }"
                                    >
                                        <div class="w-8 h-8 rounded-lg flex items-center justify-center flex-shrink-0 text-sm"
                                            :class="item.type === 'new_order' ? 'bg-amber-100 text-amber-700' : 'bg-blue-100 text-blue-700'">
                                            <i :class="item.icon || (item.type === 'new_order' ? 'fa-solid fa-bag-shopping' : 'fa-solid fa-user')"></i>
                                        </div>
                                        <div class="flex-1 min-w-0">
                                            <div class="flex items-center justify-between gap-1 mb-0.5">
                                                <p class="text-xs font-semibold text-slate-800 truncate" :class="{ 'font-bold': !item.read_at }">
                                                    {{ item.title }}
                                                </p>
                                                <span v-if="!item.read_at" class="w-2 h-2 rounded-full bg-primary flex-shrink-0"></span>
                                            </div>
                                            <p class="text-xs text-slate-600 line-clamp-2 leading-tight">
                                                {{ item.message }}
                                            </p>
                                            <span class="text-[10px] text-slate-400 mt-1 block">
                                                {{ item.created_at }}
                                            </span>
                                        </div>
                                    </div>
                                </template>
                                <div v-else class="py-8 px-4 text-center text-slate-400">
                                    <i class="fa-regular fa-bell-slash text-2xl mb-2 block opacity-40"></i>
                                    <p class="text-xs">No tienes notificaciones por ahora</p>
                                </div>
                            </div>
                        </div>
                    </div>

            <router-link v-if="pos.permission"
                class="w-9 h-9 rounded-lg flex items-center justify-center text-primary bg-primary/5"
                :to="{ path: '/admin/' + pos.url }">
                <i class="lab lab-fill-pos lab-font-size-16 font-fill-pos"></i>
            </router-link>
        </div>

        <!-- Dropdown de datos de usuario -->
        <div class="dropdown-group border border-gray-200 rounded-lg">
            <button class="dropdown-btn flex items-center gap-1">
                <img class="flex-shrink-0 w-9 h-9 object-cover rounded-lg filter-gray p-1" :src="authInfo.image" alt="avatar">
                <h3 class="whitespace-nowrap text-sm font-normal capitalize text-left leading-[12px] pr-1">{{ $t('label.hello') }} <b
                        class="block font-medium text-primary">{{ textShortener(authInfo.name, 15) }}</b></h3>
                <i class="lab lab-arrow-down text-xs ml-1.5 lab-font-size-14"></i>
            </button>
            <div class="dropdown-list fixed sm:absolute top-[65px] sm:top-12 left-3 right-3 sm:left-auto sm:right-0 w-[calc(100%_-_24px)] sm:w-[360px] z-[70] rounded-xl max-h-[calc(100vh_-_90px)] sm:max-h-none overflow-y-auto sm:h-auto p-4 shadow-paper bg-white border border-gray-100 mt-2">
                <div class="w-fit mx-auto text-center mb-5">
                    <figure
                        class="relative z-10 w-[98px] h-[98px] border-2 border-dashed rounded-full inline-flex items-center justify-center border-white bg-gradient-to-t from-[#FF7A00] to-[#FF016C] before:absolute before:top-1/2 before:left-1/2 before:-translate-x-1/2 before:-translate-y-1/2 before:w-24 before:h-24 before:rounded-full before:-z-10 before:bg-white">
                        <img class="w-[90px] h-[90px] rounded-full shadow-avatar" :src="authInfo.image"
                            alt="avatar">
                    </figure>

                    <label for="imageProperty"
                        class="block w-11 h-11 mx-auto -mt-7 mb-3 relative z-10 rounded-full border-2 cursor-pointer bg-heading border-white">
                        <input @change="saveImage" accept="image/png, image/jpeg, image/jpg" ref="imageProperty"
                            type="file" id="imageProperty"
                            class="w-full h-full rounded-full opacity-0 cursor-pointer">
                        <i
                            class="lab lab-fill-edit absolute top-1/2 left-1/2 -translate-y-1/2 -translate-x-1/2 -z-10 lab-font-size-24 lab-font-color-1 cursor-pointer"></i>
                    </label>

                    <h3 class="font-medium text-sm leading-6 capitalize mb-0.5">
                        {{ textShortener(authInfo.name, 20) }}
                    </h3>
                    <p class="text-xs mb-0.5">{{ authInfo.email }}</p>
                    <p v-if="authInfo.phone" class="text-xs" dir="ltr">{{ authInfo.country_code }}{{ authInfo.phone
                        }}</p>
                    <h3 class="font-medium text-sm leading-6 capitalize mb-0.5">{{ authInfo.currency_balance }}</h3>
                </div>
                <nav>
                    <router-link :to="{ name: 'admin.profile.editProfile' }"
                        class="paper-link transition w-full flex items-center gap-3.5 py-3 border-b last:border-none border-[#EFF0F6]">
                        <i class="lab lab-line-edit lab-font-size-17"></i>
                        <span class="text-sm leading-6 capitalize">{{ $t('button.edit_profile') }}</span>
                    </router-link>

                    <router-link :to="{ name: 'admin.profile.changePassword' }"
                        class="paper-link transition w-full flex items-center gap-3.5 py-3 border-b last:border-none border-[#EFF0F6]">
                        <i class="lab lab-line-key lab-font-size-17"></i>
                        <span class="text-sm leading-6 capitalize">{{ $t('button.change_password') }}</span>
                    </router-link>

                    <button @click="logout()"
                        class="paper-link transition w-full flex items-center gap-3.5 py-3 border-b last:border-none border-[#EFF0F6]">
                        <i class="lab lab-line-logout lab-font-size-17"></i>
                        <span class="text-sm leading-6 capitalize">{{ $t('button.logout') }}</span>
                    </button>
                </nav>
            </div>
        </div>
    </div>

    <!-- Notification Start -->
    <div id="order-modal" v-if="orderNotificationStatus" ref="orderNotificationModal" class="modal active ff-modal">
        <div class="modal-dialog max-w-[360px] p-6 text-center relative">
            <button @click.prevent="closeOrderNotificationModal('order-modal', 'modal-active')"
                class="modal-close absolute top-4 right-4">
                <i class="fa-regular fa-circle-xmark"></i>
            </button>
            <h3 class="text-[18px] font-semibold leading-8 mb-6">
                {{ orderNotificationMessage }}
                <span class="block">{{ $t('message.please_check_your_order_list') }}</span>
            </h3>
            <router-link :to="{ path: '/admin/' + orderNotification.url }"
                @click.prevent="closeOrderNotificationModal('order-modal', 'modal-active')"
                class="db-btn h-[38px] shadow-[0px_6px_10px_rgba(255,_0,_107,_0.24)] bg-primary text-white">
                {{ $t('button.let_me_check') }}
            </router-link>
        </div>
    </div>
    <!-- Notification End -->
</template>

<script>

import activityEnum from "../../../enums/modules/activityEnum";
import _ from "lodash";
import alertService from "../../../services/alertService";
import targetService from "../../../services/targetService";
import appService from "../../../services/appService";
import { initializeApp } from "firebase/app";
import { getMessaging, getToken, onMessage } from "firebase/messaging";
import axios from 'axios';

export default {
    name: "BackendNavbarComponent",
    data() {
        return {
            isSidebarActive: false,
            isScrolled: false,
            loading: {
                isActive: false,
            },
            enums: {
                activityEnum: activityEnum
            },
            defaultBranch: null,
            pos: {
                permission: false,
                url: '',
            },
            orderNotificationStatus: false,
            orderNotificationMessage: "",
            orderNotification: {
                permission: false,
                url: ""
            },
            internalNotifications: [],
            unreadCount: 0,
            notificationPollTimer: null,
        }
    },
    computed: {
        setting: function () {
            return this.$store.getters['frontendSetting/lists'];
        },
        authInfo: function () {
            return this.$store.getters.authInfo;
        },
        languages: function () {
            return this.$store.getters['frontendLanguage/lists'];
        },
        language: function () {
            return this.$store.getters['frontendLanguage/show'];
        },
        permissions: function () {
            return this.$store.getters.authPermission;
        }
    },
    mounted() {
        appService.responsiveLoad();
        this.posPermissionCheck();
        this.orderPermissionCheck();
        this.checkSidebarState();
        this.handleScroll();

        window.addEventListener('resize', this.checkSidebarState);
        document.addEventListener('click', this.checkSidebarState);

        const dbMain = document.querySelector('.db-main');
        if (dbMain) {
            dbMain.addEventListener('scroll', this.handleScroll);
        }

        this.$store.dispatch('frontendSetting/lists').then(res => {
            const defaultLanguage = res.data.data.site_default_language;
            const globalState = this.$store.getters['globalState/lists'];
            let langId = defaultLanguage;
            if (globalState.language_id > 0) {
                langId = globalState.language_id;
            }

            this.$store.dispatch('frontendLanguage/lists', {
                order_column: 'id',
                order_type: 'asc',
                status: activityEnum.ENABLE
            }).then(lRes => {
                const activeLangs = lRes.data.data || [];
                let validLang = activeLangs.find(l => Number(l.id) === Number(langId));
                if (!validLang) {
                    validLang = activeLangs.find(l => Number(l.id) === Number(defaultLanguage));
                }
                if (!validLang && activeLangs.length > 0) {
                    validLang = activeLangs[0];
                }

                if (validLang) {
                    this.$store.dispatch('frontendLanguage/show', validLang.id).then(showRes => {
                        this.$i18n.locale = showRes.data.data.code;
                        this.$store.dispatch("globalState/set", {
                            language_id: showRes.data.data.id,
                            language_code: showRes.data.data.code,
                            display_mode: showRes.data.data.display_mode
                        });
                    }).catch();
                }
            }).catch();
        }).catch();

        window.setTimeout(() => {
            if (this.$store.getters.authStatus && this.setting.notification_fcm_api_key && this.setting.notification_fcm_auth_domain && this.setting.notification_fcm_project_id && this.setting.notification_fcm_storage_bucket && this.setting.notification_fcm_messaging_sender_id && this.setting.notification_fcm_app_id && this.setting.notification_fcm_measurement_id) {
                initializeApp({
                    apiKey: this.setting.notification_fcm_api_key,
                    authDomain: this.setting.notification_fcm_auth_domain,
                    projectId: this.setting.notification_fcm_project_id,
                    storageBucket: this.setting.notification_fcm_storage_bucket,
                    messagingSenderId: this.setting.notification_fcm_messaging_sender_id,
                    appId: this.setting.notification_fcm_app_id,
                    measurementId: this.setting.notification_fcm_measurement_id
                });
                const messaging = getMessaging();

                Notification.requestPermission().then((permission) => {
                    if (permission === 'granted') {
                        getToken(messaging, { vapidKey: this.setting.notification_fcm_public_vapid_key }).then((currentToken) => {
                            if (currentToken) {
                                axios.post('/frontend/device-token/web', { token: currentToken }).then().catch((error) => {
                                    if (error.response.data.message === 'Unauthenticated.') {
                                        this.$store.dispatch('loginDataReset');
                                    }
                                });
                            }
                        }).catch();
                    }
                });

                onMessage(messaging, (payload) => {
                    const notificationTitle = payload.notification.title;
                    const notificationOptions = {
                        body: payload.notification.body,
                        icon: '/images/required/firebase-logo.png'
                    };
                    new Notification(notificationTitle, notificationOptions);

                    if (payload.data.topicName === 'new-order-found' && this.orderNotification.permission) {
                        this.orderNotificationStatus = true;
                        this.orderNotificationMessage = payload.notification.body;
                        const audio = new Audio(this.setting.notification_audio);
                        audio.play();
                    }
                });
            }
        }, 5000);

        // Notificaciones internas
        this.fetchInternalNotifications();
        this.notificationPollTimer = setInterval(() => {
            this.fetchInternalNotifications();
        }, 30000);
    },
    unmounted() {
        if (this.notificationPollTimer) {
            clearInterval(this.notificationPollTimer);
            this.notificationPollTimer = null;
        }
        window.removeEventListener('resize', this.checkSidebarState);
        document.removeEventListener('click', this.checkSidebarState);
        const dbMain = document.querySelector('.db-main');
        if (dbMain) {
            dbMain.removeEventListener('scroll', this.handleScroll);
        }
    },
    methods: {
        handleScroll: function () {
            const dbMain = document.querySelector('.db-main');
            if (dbMain) {
                this.isScrolled = dbMain.scrollTop > 5;
            }
        },
        textShortener: function (text, number = 30) {
            return appService.textShortener(text, number);
        },
        logout: function () {
            this.$store.dispatch("logout").then(res => {
                this.$router.push({ name: "frontend.home" });
            }).catch();
        },
        changeLanguage: function (id, code, mode) {
            this.defaultLanguage = id;
            this.$store.dispatch("globalState/set", {
                language_id: id,
                language_code: code,
                display_mode: mode
            }).then(res => {
                this.$store.dispatch('frontendLanguage/show', id).then(res => {
                    this.$i18n.locale = res.data.data.code;
                }).catch();
            }).catch();
        },
        posPermissionCheck: function () {
            const permissions = this.$store.getters.authPermission;
            if (permissions.length > 0) {
                _.forEach(permissions, (permission) => {
                    if (permission.name === 'pos') {
                        if (permission.access === true) {
                            this.pos.permission = true;
                            this.pos.url = permission.url;
                        }
                    }
                });
            }
        },
        orderPermissionCheck: function () {
            const permissions = this.$store.getters.authPermission;
            if (permissions.length > 0) {
                _.forEach(permissions, (permission) => {
                    if (permission.name === 'online-orders') {
                        if (permission.access === true) {
                            this.orderNotification.permission = true;
                            this.orderNotification.url = permission.url;
                        }
                    }
                });
            }
        },
        closeOrderNotificationModal: function (id, cClass) {
            targetService.hideTarget(id, cClass);
            this.orderNotificationStatus = false;
        },
        saveImage: function () {
            if (this.$refs.imageProperty.files[0]) {
                try {
                    this.loading.isActive = true;
                    const formData = new FormData();
                    formData.append("image", this.$refs.imageProperty.files[0]);
                    this.$store.dispatch("frontendEditProfile/changeImage", { form: formData }).then((res) => {
                        this.$store.dispatch('updateAuthInfo', res.data.data).then(res => {
                            this.loading.isActive = false;
                            alertService.success(this.$t("message.photo_update"));
                            this.$refs.imageProperty.value = null;
                        }).catch((err) => {
                            this.loading.isActive = false;
                            alertService.error(err);
                        });
                    }).catch((err) => {
                        this.loading.isActive = false;
                        this.imageErrors = err.response.data.errors;
                    });
                } catch (err) {
                    this.loading.isActive = false;
                    alertService.error(err.response.data.message);
                }
            }
        },
        closeSidebar: function () {
            appService.closeSidebar();
            this.$nextTick(() => {
                this.checkSidebarState();
            });
        },
        toggleSidebar: function () {
            appService.toggleSidebar();
            this.$nextTick(() => {
                this.checkSidebarState();
            });
        },
        checkSidebarState: function () {
            const sidebar = document.querySelector('.db-sidebar');
            if (sidebar) {
                this.isSidebarActive = sidebar.classList.contains('active');
            }
        },
        fetchInternalNotifications: function () {
            if (!this.$store.getters.authStatus) return;
            axios.get('/admin/internal-notifications').then((res) => {
                if (res.data && res.data.status) {
                    const previousUnread = this.unreadCount;
                    this.internalNotifications = res.data.notifications || [];
                    this.unreadCount = Number(res.data.unread_count) || 0;

                    // Si hay nuevas notificaciones que aumentaron el contador, reproducir audio si existe
                    if (this.unreadCount > previousUnread && previousUnread > 0 && this.setting && this.setting.notification_audio) {
                        try {
                            const audio = new Audio(this.setting.notification_audio);
                            audio.play().catch(() => {});
                        } catch (e) {}
                    }
                }
            }).catch(() => {});
        },
        markAllNotificationsAsRead: function () {
            axios.post('/admin/internal-notifications/mark-all-read').then((res) => {
                if (res.data && res.data.status) {
                    this.unreadCount = 0;
                    this.internalNotifications = this.internalNotifications.map(n => ({
                        ...n,
                        read_at: n.read_at || new Date().toISOString()
                    }));
                }
            }).catch(() => {});
        },
        handleNotificationClick: function (item) {
            if (!item.read_at) {
                axios.post(`/admin/internal-notifications/${item.id}/read`).then((res) => {
                    item.read_at = new Date().toISOString();
                    if (res.data && typeof res.data.unread_count !== 'undefined') {
                        this.unreadCount = res.data.unread_count;
                    } else if (this.unreadCount > 0) {
                        this.unreadCount--;
                    }
                }).catch(() => {});
            }
        }
    }
}
</script>

<style scoped>
.dropdown-list {
    transition: transform 0.25s cubic-bezier(0.16, 1, 0.3, 1), opacity 0.25s cubic-bezier(0.16, 1, 0.3, 1), visibility 0.25s ease;
    opacity: 0;
    visibility: hidden;
    transform: translateY(-14px);
    transform-origin: top center;
}

.dropdown-group:hover > .dropdown-list,
.dropdown-group:focus-within > .dropdown-list,
.dropdown-list.active {
    opacity: 1;
    visibility: visible;
    transform: translateY(0);
}
</style>
